import { computed, inject } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { useFormContext } from '/app/composables/form';

const regExpCause = new RegExp(/What(.*)could|How(.*)could/, 'i');

export const restrictedWordsCategories = [
    'entertainment-4bc2525b',
    'messaging-25b464dd',
];

export const restrictedWords = [
    'unexpected',
    'unique',
    'unusual',
    'new',
    'surprising'
];

export const useChallengeCreationFlow = createSharedComposable(() => {
    const form = useFormContext();
    const context = inject('context');
    const builder = inject('builder', null);

    const challenge = computed(() => form.fields.challenge);
    const hasSlug = computed(() => !!challenge.value.slug);

    const isCaption = computed(() => {
        const categories = ['Funny', 'Inspiring', 'Insightful', 'Shocking'];
        return challenge.value.caption_solution_format === 'caption'
            || categories.includes(challenge.value.main_category);
    });

    const completedCategory = computed(() => !!challenge.value.platform_category || hasSlug.value);
    const completedImage = computed(() => !!challenge.value.primary_image || hasSlug.value);
    const completedDuration = computed(() => !!(challenge.value.duration_start && challenge.value.duration_end) || hasSlug.value);
    const completedPrize = computed(() => !!(challenge.value.prize_type && challenge.value.prize_money !== null) || hasSlug.value);
    const completedCaptionFormat = computed(() => !!challenge.value.caption_solution_format || hasSlug.value);
    const completedTitle = computed(() => isCaption.value || regExpCause.test(challenge.value.question_title) || hasSlug.value);
    const completedQQQuestion = computed(() => !!(challenge.value.question_title && challenge.value.question_detail) || hasSlug.value);
    const completedIdea = computed(() => !!(form.fields.idea.description && form.fields.idea.title) || hasSlug.value);
    const completedInnovation = computed(() => !!challenge.value.innovation_scale || hasSlug.value);
    const completedArticle = computed(() => !!challenge.value.article || form.fields.article_skiped || hasSlug.value);
    const completedAssumptions = computed(() => !!(hasWho.value && hasWhy.value && hasWhat.value) || hasSlug.value);
    const completedPurpose = computed(() => !!form.fields.idea.purpose || hasSlug.value);

    const hasWho = computed(() => !!challenge.value.assumption_who);
    const hasWhy = computed(() => !!challenge.value.assumption_why);
    const hasWhat = computed(() => !!challenge.value.assumption_what);
    const hasQuestion = computed(() => {
        return builder?.isCaption ||
            (!builder?.isQQ
                ? regExpCause.test(challenge.value.question_title)
                : !!challenge.value.question_title
            );
    });
    const hasCategory = computed(() => !!challenge.value.platform_category);
    const hasIdea = computed(() => !!(form.fields.idea.title && form.fields.idea.description));
    const hasAssumptions = computed(() => hasWho.value || hasWhy.value || hasWhat.value);
    const hasDuration = computed(() => !!(challenge.value.duration_start && challenge.value.duration_end));
    const hasPrize = computed(() => !!challenge.value.prize_type);
    const hasImage = computed(() => !!challenge.value.primary_image);
    const hasCaption = computed(() => !!challenge.value.caption_solution_format);
    const hasPurpose = computed(() => !!form.fields.idea.purpose);


    const isValid = computed(() => {
        return ![
            hasQuestion.value,
            hasCategory.value,
            hasImage.value,
            hasDuration.value,
            hasPrize.value,
        ].some(v => !v);
    });

    const isImproveIdea = computed(() => challenge.value.solution_format === 'improve-an-idea');

    const isValidQuestionTitle = computed(() => {
        const checkWordsRestriction = !!challenge.value.solution_format || restrictedWordsCategories.some(category =>
            context.categoryMain && context.categoryMain.reference.includes(category),
        );

        return checkWordsRestriction
            ? ['unexpected', 'unique', 'unusual', 'surprising'].some(
                word => challenge.value.question_title.match(new RegExp(word, 'i'))
            )
            : true;
    });

    return {
        challenge,
        hasSlug,
        completedCategory, completedImage, completedDuration, completedPrize,
        completedCaptionFormat, completedTitle, completedQQQuestion,
        completedIdea, completedInnovation, completedArticle,
        completedAssumptions, completedPurpose,
        hasWho, hasWhy, hasWhat, hasQuestion, hasCategory, hasIdea,
        hasAssumptions, hasDuration, hasPrize, hasImage, hasCaption, hasPurpose,
        isCaption, isValid, isImproveIdea, isValidQuestionTitle,
    };
});
